import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';
import type Keyframes from 'styled-components/dist/models/Keyframes';

import type { RegistrationConfig } from '@/modules/registration/helpers/types';

import { device, hexToRGB } from '@/styles/helpers';

type ButtonAnimation = {
  keyframes: Keyframes;
  duration: number;
  delay?: number;
};
export interface ScButtonProps {
  animation?: ButtonAnimation;
  link?: boolean;

  primary?: boolean;
  primaryTransparent?: boolean;
  secondary?: boolean;
  secondaryTransparent?: boolean;
  blackTransparent?: boolean;
  white?: boolean;
  whiteTransparent?: boolean;
  grayTransparent?: boolean;
  transparent?: boolean;
  grayTransparentBg?: boolean;
  gold?: boolean;
  sandGold?: boolean;
  secondaryGreenGradient?: boolean;
  secondaryGrayGradient?: boolean;
  navigation?: boolean;

  disable?: boolean;

  xSmall?: boolean;
  small?: boolean;
  large?: boolean;

  autoPaddingHorizontal?: boolean;
  wide?: boolean;
  noMinWidth?: boolean;

  horizontalAlignment?: boolean;

  height?: string;
  borderRadius?: string;
  paddingX?: number;

  isMobileDevice: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
  $buttons?: RegistrationConfig['buttons'];
}

const bgFillAnimation = (bgColor: string) => keyframes`
    0%, 99%   { background-color: transparent; }
    100% { background-color: ${bgColor}; }
  `;

export const ScButton = styled.button<ScButtonProps>`
  display: inline-block;
  align-items: center;
  justify-content: center;
  position: relative;
  border: none;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 50px;
  border-width: 2px;
  border-style: solid;
  cursor: pointer;
  transition:
    box-shadow 0.3s,
    background-color 0.3s,
    border-color 0.3s,
    color 0.3s;
  padding: ${({ paddingX }) =>
    paddingX ? `0px ${paddingX}px 1px` : '0px 16px 1px 16px'};
  z-index: 0;
  overflow: hidden;

  .text {
    position: relative;
    z-index: 1;

    .play-btn-content {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      color: ${({ theme }) => theme.whiteColor};
      text-transform: uppercase;
      font-weight: bold;
      font-size: 14px;
      border-radius: 40px;
      & > img {
        width: 15%;
        height: 37.5%;
        max-width: 16px;
        max-height: 18px;
        @media (max-width: 408px) {
          width: 20%;
          height: 50%;
        }
      }
      & > span {
        flex-shrink: 0;
      }
      @media (${device.tablet}) {
        font-size: 16px;
      }
      @media (${device.mobileXS}) {
        font-size: 14px;
      }
    }
  }

  .awareWrap {
    overflow: hidden;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: auto;
    height: auto;
    border-radius: 50px;
    position: absolute;
    overflow: hidden;
    display: flex;
    @media (max-width: 991px) {
      display: none;
    }
  }
  .aware {
    position: absolute;
    display: block;
    width: 0;
    height: 0;
    border-radius: 50px;
    transform: translate(-50%, -50%);
    transition:
      width 0.3s linear,
      height 0.3s linear,
      background-color 0.3s;
    z-index: 0;
  }
  &:hover {
    .aware {
      width: 250%;
      height: 250%;
    }
  }
  &:active {
    opacity: 0.8;
  }

  ${({ horizontalAlignment }) =>
    horizontalAlignment &&
    css`
      margin: 0 auto;
      display: flex;
    `}

  .text {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ animation }) =>
    animation &&
    css`
      animation: ${animation.keyframes} ${animation.duration}s
        ${animation.delay}s ease-in-out infinite;
    `}

  // Styles for button size default
  ${({ xSmall, small, large }) =>
    !(small || large || xSmall) &&
    css`
      font-size: 14px;
      min-width: 144px;
      height: 48px;
    `}

  // Styles for button size xSmall
  ${({ xSmall }) =>
    xSmall &&
    css`
      font-size: 14px;
      min-width: 128px;
      height: 32px;
    `}

  // Styles for button size small
  ${({ small }) =>
    small &&
    css`
      font-size: 14px;
      min-width: 128px;
      height: 40px;
    `}

  // Styles for button size large
  ${({ large }) =>
    large &&
    css`
      font-size: 16px;
      min-width: 192px;
      height: 48px;
    `}

  // Styles for button height
  ${({ height }) =>
    height &&
    css`
      height: ${height};
    `}

  // autoPaddingHorizontal
  ${({ autoPaddingHorizontal }) =>
    autoPaddingHorizontal &&
    css`
      min-width: auto;
    `}

  // Styles for button type wide
  ${({ wide }) =>
    wide &&
    css`
      display: block;
      width: 100%;
    `}

  // Styles for auto width button
  ${({ noMinWidth }) =>
    noMinWidth &&
    css`
      min-width: auto;
    `}

  // Styles for button type default
  ${({
    primary,
    secondary,
    white,
    theme,
    gold,
    sandGold,
    secondaryGreenGradient,
    secondaryGrayGradient,
  }) =>
    !(
      primary ||
      secondary ||
      white ||
      gold ||
      sandGold ||
      secondaryGreenGradient ||
      secondaryGrayGradient
    ) &&
    css`
      border-color: ${theme.whiteColor};
      background-color: transparent;
      color: ${theme.whiteColor};
      .aware {
        background-color: white;
      }
      &:not(:disabled):hover {
        color: ${theme.blackColor};
      }
      @media (${device.mobile}) {
        &:not(:disabled):hover {
          background-color: transparent;
          color: ${theme.whiteColor};
        }
      }
    `}

    // Styles for button type link
    ${({ link, theme, styleScheme }) =>
      link &&
      css`
      width: auto;
      height: auto;
      text-transform: none;
      font-weight: normal;
      border: none;
      background-color: transparent;
      color: ${theme.secondaryColor};

      ${
        styleScheme?.footer?.fontColor &&
        css`
        color: ${styleScheme?.footer.fontColor};
      `
      }

      &:not(:disabled):hover {
        color: ${theme.secondaryColorHover};

        ${
          styleScheme?.footer?.fontColor &&
          css`
          color: ${styleScheme?.footer.fontColor};
          opacity: 0.8;
        `
        }
      }
    `}

    // Styles for button type primary
    ${({ primary, theme }) =>
      primary &&
      css`
      border-color: ${theme.mainColor};
      background-color: ${theme.mainColor};
      color: ${theme.whiteColor};
      &:before {
        top: 0px;
        bottom: 0;
        left: 0;
        right: 0;
        content: '';
        background-color: transparent;
        position: absolute;
        border-radius: 50px;
      }
      .aware {
        background-color: ${theme.mainColorHover};
      }
      &:hover {
        border-color: ${theme.mainColorHover};
      }
      &:active {
        opacity: 1;
        border-color: ${theme.mainColorActive};
        .text {
          opacity: 0.8;
        }
        .aware {
          background-color: ${theme.mainColorActive};
        }
      }
    `}

		// Styles for button type primaryTransparent
    ${({ primaryTransparent, theme }) =>
      primaryTransparent &&
      css`
      position: relative;
      border: none;
      border-radius: 40px;
      background: linear-gradient(
        112.71deg,
        rgba(255, 173, 1, 0.2) 9.93%,
        rgba(255, 226, 37, 0.2) 46.65%,
        rgba(255, 173, 1, 0.2) 80.43%
      );

      .bg {
        display: block;
        position: absolute;
        z-index: 1;
        inset: 0;
        border-radius: 40px;
        padding: 2px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            112.71deg,
            #ffad01 9.93%,
            #ffe225 46.65%,
            #ffad01 80.43%
          );
        }
      }

      .aware {
        background: linear-gradient(
          112.71deg,
          rgba(255, 173, 1, 0.4) 9.93%,
          rgba(255, 226, 37, 0.4) 46.65%,
          rgba(255, 173, 1, 0.4) 80.43%
        );
      }

      &:not(:disabled):hover {
        color: ${theme.whiteColor};
      }
    `}


    // Styles for button type secondary
    ${({ secondary, theme }) =>
      secondary &&
      css`
      border-color: ${theme.secondaryColor};
      background-color: ${theme.secondaryColor};
      color: ${theme.whiteColor};
      .aware {
        background-color: ${theme.secondaryColorHover};
      }
      &:hover {
        background-color: ${theme.secondaryColor};
        border-color: ${theme.secondaryColorHover};
      }
      &:active {
        opacity: 1;
        border-color: ${theme.secondaryColorActive};
        .text {
          opacity: 0.8;
        }
        .aware {
          background-color: ${theme.secondaryColorActive};
        }
      }
    `}

    // Styles for button type secondaryTransparent
    ${({ secondaryTransparent, theme }) =>
      secondaryTransparent &&
      css`
      border-color: ${theme.secondaryColor};
      background-color: transparent;
      color: ${theme.secondaryColor};
      .aware {
        background-color: ${theme.secondaryColor};
      }
      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        @media (max-width: 991px) {
          color: ${theme.secondaryColor};
        }
      }
    `}

		// Styles for button type blackTransparent
    ${({ blackTransparent, theme, $buttons }) =>
      blackTransparent &&
      css`
      border-color: ${theme.blackColor};
      background-color: transparent;
      color: ${theme.blackColor};

      ${
        $buttons?.borderRadius &&
        css`
        border-radius: ${$buttons?.borderRadius};
      `
      }

      .aware {
        background-color: ${theme.blackColor};
      }

      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        animation: ${bgFillAnimation(theme.blackColor)} 0.3s;
        @media (max-width: 991px) {
          color: ${theme.blackColor};
        }
      }
    `}

    // Styles for button type white
    ${({ white, theme }) =>
      white &&
      css`
      border-color: ${theme.whiteColor};
      background-color: ${theme.whiteColor};
      color: ${theme.blackColor};
    `}

    // Styles for button type whiteTransparent
    ${({ whiteTransparent, theme, isMobileDevice }) =>
      whiteTransparent &&
      css`
      border-color: ${theme.whiteColor};
      background-color: transparent;
      color: ${theme.whiteColor};

      ${() =>
        !isMobileDevice &&
        css`
          &:not(:disabled):hover {
            animation: ${({ theme }) => bgFillAnimation(theme.whiteColor)} 0.3s
              forwards;
          }
        `}
    `}

    // Styles for button type transparent
    ${({ transparent, theme }) =>
      transparent &&
      css`
      border-color: transparent;
      background-color: transparent;
      color: ${theme.blackColor};

      &:not(:disabled):hover {
        background-color: transparent;
        color: ${theme.blackColor};
      }
    `}

    // Styles for button type grayTransparent
  ${({ grayTransparent, theme }) =>
    grayTransparent &&
    css`
      border-color: ${theme.grayColor};
      color: ${theme.grayColor};
      .aware {
        background-color: ${theme.grayColor};
      }
      &:not(:disabled):hover {
        color: ${theme.whiteColor};
        @media (max-width: 991px) {
          color: ${theme.grayColor};
        }
      }
    `}

    // Styles for button type grayTransparentBg
  ${({ grayTransparentBg, theme }) =>
    grayTransparentBg &&
    css`
      border: none;
      color: ${theme.whiteColor};
      background-color: rgba(255, 255, 255, 0.2);
      .aware {
        background-color: rgba(255, 255, 255, 0.2);
      }
      @media (${device.mobile}) {
        &:hover {
          background-color: rgba(255, 255, 255, 0.4);
          border-color: ${theme.secondaryColorHover};
        }
      }

      &:not(:disabled):hover {
        color: ${theme.whiteColor};
      }
    `}

    // Styles for button type gold
    ${({ gold, theme }) =>
      gold &&
      css`
      border: none;
      color: ${theme.cedarWoodColor};
      background: ${theme.goldGradient};

      @media (hover: hover) {
        &:not(:disabled):hover {
          background:
            linear-gradient(
              0deg,
              ${hexToRGB(theme.picassoColor, 0.5)} 0%,
              ${hexToRGB(theme.picassoColor, 0.5)} 100%
            ),
            ${theme.goldGradient};
        }
      }
    `}

    // Styles for button type secondaryGreenGradient
    ${({ secondaryGreenGradient, theme }) =>
      secondaryGreenGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: ${theme.secondaryGreenGradient};
      transition: opacity 0.2s ease-in-out;
      border-radius: 12px;
      box-shadow: 0 0 15px ${theme.greenDarkGradient};
      z-index: 2;
      border-radius: 12px;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        border-radius: 12px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    // Styles for button type sandGold
    ${({ sandGold, theme }) =>
      sandGold &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: ${theme.sandGold};
      border-radius: 12px;
      transition: opacity 0.2s ease-in-out;
      z-index: 2;


      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        border-radius: 12px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    // Styles for button type secondaryGrayGradient
    ${({ secondaryGrayGradient, theme }) =>
      secondaryGrayGradient &&
      css`
      position: relative;
      border: none;
      color: ${theme.whiteColor};
      background: rgba(255, 255, 255, 0.2);
      transition: opacity 0.2s ease-in-out;
      border-radius: 12px;
      box-shadow: 0 0 15px ${theme.greenDarkGradient};
      z-index: 2;
      border-radius: 12px;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        padding: 2px;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0.3),
          rgba(255, 255, 255, 0)
        );
        border-radius: 12px;
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: 2;
      }
      @media (hover: hover) {
        &:not(:disabled):hover {
          opacity: 0.9;
        }
      }
    `}

    // Styles for button type navigation
    ${({ navigation, theme }) =>
      navigation &&
      css`
      padding: 0 13px 0 0;
      overflow: visible;

      text-transform: uppercase;
      border: none;
      background-color: transparent;
      color: ${theme.secondaryColor};
      height: 24px;
      &:not(:disabled):hover {
        color: ${theme.secondaryColorHover};
      }

      .text {
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
      }

      .awareWrap {
        display: none;
      }
    `}

    // Styles for disabled button
    ${({ disable, theme }) =>
      disable &&
      css`
      cursor: default;
      border-color: ${theme.grayLightColor};
      background-color: ${theme.grayLightColor};
      color: ${theme.grayColor};
      &:hover {
        box-shadow: none;
      }
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    .awareWrap {
      display: none;
    }
  }

  ${({ borderRadius }) =>
    borderRadius &&
    css`
      border-radius: ${borderRadius};
    `}

  ${({ $buttons }) =>
    $buttons?.wrapBorderRadius &&
    css`
      .awareWrap {
        border-radius: ${$buttons.wrapBorderRadius};
      }
    `}
`;

export const ScLink = styled(Link)`` as typeof Link;

export const ScButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 13px;

  ${ScButton} {
    .text {
      color: ${({ theme }) => theme.secondaryColor};
      transition: color 0.3s;
    }
    padding: 0;
    height: fit-content;
    border: none;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    .awareWrap {
      display: none;
    }
  }

  .blue-arrow-icon {
    transition: transform 0.3s;
    color: ${({ theme }) => theme.secondaryColor};
  }
  @media (hover: hover) {
    &:hover {
      ${ScButton} {
        .text {
          color: ${({ theme }) => theme.secondaryColorHover};
        }
      }
    }
    &:has(button:hover) .blue-arrow-icon {
      transform: translateX(3px);
      color: ${({ theme }) => theme.secondaryColorHover};
    }
  }
`;
